import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BookOpen,
  Star,
  TrendingUp,
  Image,
  Save,
  RefreshCw,
  Bookmark,
  Hash,
  AlertCircle,
  CheckCircle2,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import { server } from "../server";

const AddSubject = () => {
  const [selectedExams, setSelectedExams] = useState([]);
  const [chapters, setChapters] = useState([""]);
  const [topics, setTopics] = useState([""]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      subject: "",
      description: "",
      icon: "",
      isPopular: false,
      exams: [],
      chapters: [""],
      topics: [""],
    },
  });

  // Popular exam options
  const examOptions = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "CAT",
    "GATE",
    "SSC",
    "Bank PO",
    "CLAT",
    "NDA",
    "CDS",
    "AIIMS",
    "NET",
    "SET",
    "State PSC",
  ];

  // Icon options
  const iconOptions = [
    "📚",
    "🔬",
    "🧮",
    "🌍",
    "💻",
    "🧪",
    "📊",
    "📈",
    "🔍",
    "💡",
    "🎯",
    "⚡",
    "🌟",
    "🔥",
    "💎",
    "🎓",
  ];

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      const payload = {
        subject: data.subject,
        description: data.description,
        exams: selectedExams,
        icon: data.icon,
        isPopular: data.isPopular,
      };

      const { data: resData } = await axios.post(
        `${server}/subject/new`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(resData?.message);
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create subject");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    reset();
    setSelectedExams([]);
    setChapters([""]);
    setTopics([""]);
  };

  // Handle exam selection
  const handleExamSelect = (exam) => {
    if (selectedExams.includes(exam)) {
      setSelectedExams(selectedExams.filter((e) => e !== exam));
    } else {
      setSelectedExams([...selectedExams, exam]);
    }
  };

  // Handle chapter input
  const handleChapterChange = (index, value) => {
    const newChapters = [...chapters];
    newChapters[index] = value;
    setChapters(newChapters);
  };

  const addChapter = () => {
    setChapters([...chapters, ""]);
  };

  const removeChapter = (index) => {
    if (chapters.length > 1) {
      const newChapters = chapters.filter((_, i) => i !== index);
      setChapters(newChapters);
    }
  };

  // Handle topic input
  const handleTopicChange = (index, value) => {
    const newTopics = [...topics];
    newTopics[index] = value;
    setTopics(newTopics);
  };

  const addTopic = () => {
    setTopics([...topics, ""]);
  };

  const removeTopic = (index) => {
    if (topics.length > 1) {
      const newTopics = topics.filter((_, i) => i !== index);
      setTopics(newTopics);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    tap: { scale: 0.95 },
    hover: { scale: 1.05 },
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-2xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">Add New Subject</h1>
          </div>
          <p className="text-gray-400 text-lg">
            Create a new subject with relevant exams, topics, and chapters
          </p>
        </motion.div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Subject Name */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Hash className="w-5 h-5 text-blue-400" />
              Subject Name *
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white placeholder-gray-500 bg-gray-700 ${
                errors.subject
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
              placeholder="Enter subject name (e.g., Mathematics, Physics, Chemistry...)"
              {...register("subject", {
                required: "Subject name is required",
                minLength: {
                  value: 2,
                  message: "Subject name must be at least 2 characters",
                },
              })}
            />
            {errors.subject && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-sm text-red-400 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.subject.message}
              </motion.p>
            )}
          </motion.div>

          {/* Description */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Description *
            </label>
            <textarea
              rows={4}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white placeholder-gray-500 bg-gray-700 resize-none ${
                errors.description
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
              placeholder="Enter a detailed description of the subject..."
              {...register("description", {
                required: "Description is required",
                minLength: {
                  value: 10,
                  message: "Description must be at least 10 characters",
                },
                maxLength: {
                  value: 500,
                  message: "Description must not exceed 500 characters",
                },
              })}
            />
            <div className="flex justify-between items-center mt-2">
              {errors.description ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-red-400 flex items-center gap-1"
                >
                  <AlertCircle className="w-4 h-4" />
                  {errors.description.message}
                </motion.p>
              ) : (
                <div className="text-sm text-gray-400">
                  {watch("description")?.length || 0}/500 characters
                </div>
              )}
            </div>
          </motion.div>

          {/* Icon Selection */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-400" />
              Choose Icon
            </label>
            <div className="grid grid-cols-8 gap-2">
              {iconOptions.map((icon, index) => (
                <motion.button
                  key={index}
                  type="button"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-3 text-2xl rounded-xl border-2 transition-all duration-200 bg-gray-700 ${
                    watch("icon") === icon
                      ? "border-blue-500 bg-blue-500/20"
                      : "border-gray-600 hover:border-gray-500"
                  }`}
                  onClick={() => setValue("icon", icon)}
                >
                  {icon}
                </motion.button>
              ))}
            </div>
            <input type="hidden" {...register("icon")} />
            {watch("icon") && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-3 p-3 bg-gray-700 rounded-lg border border-gray-600"
              >
                <p className="text-sm text-gray-400">Selected Icon:</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xl">{watch("icon")}</span>
                  <span className="text-white font-medium">
                    {watch("icon")}
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Exams Selection */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-blue-400" />
              Select Relevant Exams *
            </label>
            <p className="text-gray-400 text-sm mb-4">
              Choose exams that include this subject
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {examOptions.map((exam) => (
                <motion.label
                  key={exam}
                  className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 bg-gray-700 ${
                    selectedExams.includes(exam)
                      ? "border-blue-500 bg-blue-500/20 text-blue-300"
                      : "border-gray-600 hover:border-gray-500 text-gray-300"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={selectedExams.includes(exam)}
                    onChange={() => handleExamSelect(exam)}
                  />
                  <div
                    className={`w-5 h-5 border-2 rounded transition-all duration-200 flex items-center justify-center ${
                      selectedExams.includes(exam)
                        ? "border-blue-500 bg-blue-500"
                        : "border-gray-500 bg-gray-600"
                    }`}
                  >
                    {selectedExams.includes(exam) && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </motion.div>
                    )}
                  </div>
                  <span className="text-sm font-medium">{exam}</span>
                </motion.label>
              ))}
            </div>
            {selectedExams.length === 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 text-sm text-red-400"
              >
                Please select at least one exam
              </motion.p>
            )}
            {selectedExams.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30"
              >
                <p className="text-sm text-blue-300 font-medium">
                  Selected Exams: {selectedExams.join(", ")}
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Popular Subject Toggle */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <span className="block text-sm font-semibold text-white">
                    Mark as Popular Subject
                  </span>
                  <span className="block text-sm text-gray-400 mt-1">
                    This subject will be highlighted as popular
                  </span>
                </div>
              </div>
              <input
                type="checkbox"
                className="hidden"
                {...register("isPopular")}
              />
              <div
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  watch("isPopular") ? "bg-green-500" : "bg-gray-600"
                }`}
              >
                <motion.div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg ${
                    watch("isPopular") ? "left-7" : "left-1"
                  }`}
                  layout
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              </div>
            </label>

            {watch("isPopular") && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-3 p-3 bg-orange-500/20 rounded-lg border border-orange-500/30"
              >
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-orange-400 fill-current" />
                  <span className="text-sm text-orange-300 font-medium">
                    This subject will be marked as popular
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Form Actions */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 pt-6"
            variants={itemVariants}
          >
            <motion.button
              type="submit"
              disabled={isSubmitting || selectedExams.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-br from-blue-600 to-purple-700 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              variants={buttonVariants}
              whileHover={
                !isSubmitting && selectedExams.length > 0 ? { scale: 1.02 } : {}
              }
              whileTap={
                !isSubmitting && selectedExams.length > 0 ? { scale: 0.98 } : {}
              }
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </motion.div>
                  Adding Subject...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Add Subject
                </>
              )}
            </motion.button>

            <motion.button
              type="button"
              onClick={resetForm}
              className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-5 h-5" />
              Reset Form
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddSubject;
